import type { AnyRouter } from '@tanstack/react-router';

import { createTracker, initAnalytics as initTracker } from '@optiro/analytics';

import type { userData } from '@/types/types';

type Origin = userData['company']['origin'];

// The closed list lives in specs/analytics.md: a new event is added there first.
type Events = {
  signup_started: undefined;
  signup_completed: { origin: Origin };
  project_created: undefined;
  task_created: undefined;
  booking_created: undefined;
  dependency_added: undefined;
  closed_day_set: undefined;
  share_link_created: undefined;
  plan_pdf_generated: undefined;
  partner_invited: undefined;
  user_invited: undefined;
  progress_reported: undefined;
  tender_document_uploaded: undefined;
  tasks_view_switched: { view: 'list' | 'planning' };
};

export type EventName = keyof Events;

// Delingslink pages, print views, feedback token links and Optiro-admin send nothing.
const IGNORED_PATHS = ['/print', '/shared', '/feedback', '/admin'];

const tracker = createTracker<Events>();

export const track = tracker.track;

export function initAnalytics() {
  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;

  initTracker({
    enabled: import.meta.env.PROD && Boolean(domain),
    domain: domain ?? '',
    endpoint: '/_t/e',
    autoCapturePageviews: false,
    ignorePaths: IGNORED_PATHS,
  });
}

/** Role, virksomhedsstatus and oprindelse for signed-in users; nothing for signed out or Optiro-admin. */
export function setTrackingContext(user: userData | null) {
  if (!user || user.admin) {
    tracker.setContext(null);
    return;
  }

  tracker.setContext({
    ...(user.role ? { role: user.role } : {}),
    company_status: user.company.status,
    origin: user.company.origin,
  });
}

/** `/app/projects/$projectId/gantt` becomes `/app/projects/:projectId/gantt`, so no ids reach Plausible. */
export function routeTemplate(fullPath: string) {
  return fullPath.replace(/\$(\w+)/g, ':$1').replace(/\$$/, '*');
}

function isIgnored(pathname: string) {
  return IGNORED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** One pageview per path change, with the route template. A search-only change sends none. */
export function trackRouterPageviews(router: AnyRouter) {
  return router.subscribe('onResolved', ({ pathChanged, toLocation }) => {
    if (!pathChanged || isIgnored(toLocation.pathname)) return;

    const lastMatch = router.state.matches.at(-1);
    const route = lastMatch && router.routesById[lastMatch.routeId];
    if (!route) return;

    tracker.trackPageview(routeTemplate(route.fullPath));
  });
}
